import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Stack } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { ApolloError } from '@apollo/client';

import {
  FileUploadIcon,
  InfoBox,
  snackbar,
  TableCell,
  TableHeader,
  TableRow,
  Typography,
} from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { useUserSelector } from '@occc-app/App/AppContext';
import { User_Role } from '@occc/graphql';
import { RefetchOffer } from '@occc-app/offerHelpers/types';
import { getErrorMessage } from '@occc/utils';

import { PROMO_CODE_GROUPS_POLL_INTERVAL_MS } from './promocodes-configuration.constants';
import {
  canDownloadPromoCodeGroupFiles,
  hasUnfinishedPromoCodeGroups,
  validatePromoCodesCsvFile,
} from './promocodes-individual-configuration.utils';
import { PromoCodeGroupFormValues } from './promocodes-configuration.types';

import { PromocodesIndividualTable } from '../PromocodesIndividualTable';
import { usePromocodesUploadFile } from '../../hooks/use-promocodes-upload-file.hook';
import { usePromoCodesUploadProcess } from '../../hooks/promocodes-upload.mutation.hook';
import { messages } from '../../promocodes.messages';

type PromocodesIndividualConfigurationProps = {
  promoCodeGroups: PromoCodeGroupFormValues[];
  offerId: string;
  isRefetching?: boolean;
  onRefetchOffer: RefetchOffer;
  onBeforeFileUpload?: () => Promise<void>;
  onAfterFileUploaded?: () => Promise<void>;
  onGroupDeleted?: (groupId: string) => void;
};

export const PromocodesIndividualConfiguration = ({
  promoCodeGroups,
  offerId,
  onRefetchOffer,
  isRefetching,
  onBeforeFileUpload,
  onAfterFileUploaded,
  onGroupDeleted,
}: PromocodesIndividualConfigurationProps) => {
  const { isEditable } = useCanEditFormFieldsContext();
  const user = useUserSelector();
  const isAdminUser = user?.userRole === User_Role.Admin;
  const canDownloadFiles = isEditable || canDownloadPromoCodeGroupFiles(user?.userRole);
  const canManageFiles = isEditable && isAdminUser;

  const { uploadFile } = usePromocodesUploadFile();
  const [processPromoCodesUpload] = usePromoCodesUploadProcess();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState<string | null>(null);

  const isUploadDisabled = !canManageFiles || isUploading;

  useEffect(() => {
    if (!hasUnfinishedPromoCodeGroups(promoCodeGroups) || isUploading) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const schedulePoll = () => {
      timeoutId = setTimeout(async () => {
        if (cancelled) {
          return;
        }

        try {
          await onRefetchOffer(true);
        } catch {
          // Intentionally ignore polling errors; the next poll will retry.
        } finally {
          if (!cancelled) {
            schedulePoll();
          }
        }
      }, PROMO_CODE_GROUPS_POLL_INTERVAL_MS);
    };

    schedulePoll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [promoCodeGroups, isUploading, onRefetchOffer]);

  const showUploadError = (message?: string) => {
    snackbar.error(
      message ||
        'Only a single CSV file is accepted. Please ensure the file has the correct format.',
    );
  };

  const handleFileUpload = useCallback(
    async (file: File) => {
      setIsUploading(true);
      setUploadingFileName(file.name);

      try {
        await onBeforeFileUpload?.();

        const uploadResult = await uploadFile(file);

        if (!uploadResult) {
          return;
        }

        await processPromoCodesUpload({
          variables: {
            input: {
              offerId,
              promoCodeGroupId: uploadResult.promoCodeGroupId,
            },
          },
        });

        await onAfterFileUploaded?.();
      } catch (error) {
        snackbar.error(getErrorMessage(error as ApolloError));
      } finally {
        setIsUploading(false);
        setUploadingFileName(null);
      }
    },
    [offerId, onAfterFileUploaded, onBeforeFileUpload, processPromoCodesUpload, uploadFile],
  );

  const onDrop = useCallback(
    async (files: File[]) => {
      if (files.length !== 1) {
        showUploadError();
        return;
      }

      const file = files[0];
      const isValidCsv = await validatePromoCodesCsvFile(file);

      if (!isValidCsv) {
        showUploadError(messages.invalidCsvSingleColumn);
        return;
      }

      handleFileUpload(file);
    },
    [handleFileUpload],
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    noClick: true,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    noDrag: isUploadDisabled,
    disabled: isUploadDisabled,
  });

  const handleUploadCsvClick = () => {
    open();
  };

  return (
    <Box {...getRootProps()}>
      <input {...getInputProps()} />

      <Box sx={{ mx: -3, mb: -3 }}>
        <TableHeader
          title={
            <Typography variant='subtitle1' paragraph>
              Promo Codes
            </Typography>
          }
          disableTitleTypography
          actions={
            <LoadingButton
              type='button'
              variant='contained'
              startIcon={<FileUploadIcon />}
              onClick={handleUploadCsvClick}
              disabled={isUploadDisabled}
              loading={isUploading}
            >
              UPLOAD CSV
            </LoadingButton>
          }
        />
        <InfoBox type='info' sx={{ m: 3 }}>
          <Stack>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Upload a .csv file with one column of promo codes and a header row.
            </Typography>
            <Box component='ul' sx={{ m: 0, pl: 2.5 }}>
              <Typography component='li' variant='body2'>
                Single column only; no extra or blank columns
              </Typography>
              <Typography component='li' variant='body2'>
                Codes must be 50 characters or fewer
              </Typography>
            </Box>
          </Stack>
        </InfoBox>
        <PromocodesIndividualTable
          promoCodeGroups={promoCodeGroups}
          isDownloadEnabled={canDownloadFiles}
          isDeleteEnabled={canManageFiles}
          isRefetching={isRefetching}
          onGroupDeleted={onGroupDeleted}
        >
          {isUploading && uploadingFileName && (
            <TableRow>
              <TableCell>{uploadingFileName}</TableCell>
              <TableCell colSpan={4}>Loading...</TableCell>
            </TableRow>
          )}
        </PromocodesIndividualTable>
      </Box>
    </Box>
  );
};
